import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, PLATFORM_ID, HostListener, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { BottomSheetModalComponent, prefersReducedMotion } from '@cnt-workspace/ui';
import {
  AuthService,
  IMyRvProfile,
  IPublicUser,
  ITripStop,
  ListingAddressGeocodingService,
  RV_TYPES,
  TripPlannerService,
  addMyRvProfile,
  autoTripName,
  formatDateRange,
  formatIsoDate,
  getActiveRvProfileId,
  listMyRvProfiles,
  rvTypeLabel,
  setActiveRvProfile,
} from '@cnt-workspace/data-access';
import { HeroDatePickerComponent } from './hero-date-picker.component';
import { HeroLocationInputComponent, IHeroLocationResult } from './hero-location-input.component';

/** Local mirror of MatDatepicker's DateRange shape so the eager
 *  home-hero bundle doesn't have to pull in the Material runtime
 *  just for the type + constructor of a date pair. */
interface IDateRange { start: Date | null; end: Date | null; }

// P86 — gsap + ScrollTrigger are dynamic-imported inside ngAfterViewInit.
// The hero's initial state is now CSS-driven (see .hero-span and
// .sticky-content in the SCSS) so deferring gsap doesn't cause a
// visible pop.
import type { gsap as GsapNS } from 'gsap';
import type { ScrollTrigger as ScrollTriggerType } from 'gsap/ScrollTrigger';

@Component({
  selector: 'cnt-home-hero',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, HeroDatePickerComponent, HeroLocationInputComponent, BottomSheetModalComponent],
  templateUrl: './home-hero.component.html',
  styleUrl: './home-hero.component.scss'
})
export class HomeHeroComponent implements AfterViewInit, OnDestroy {
  private platformId = inject<object>(PLATFORM_ID);
  private router = inject(Router);
  private authService = inject(AuthService);
  private tripPlanner = inject(TripPlannerService);
  private geocoding = inject(ListingAddressGeocodingService);

  @ViewChild('heroVideo') heroVideoRef!: ElementRef<HTMLVideoElement>;

  private heroUpdate: (() => void) | null = null;
  private dateCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Populated by the dynamic-import inside ngAfterViewInit. */
  private gsap!: typeof GsapNS;
  private ST!: typeof ScrollTriggerType;

  searchMode: 'destination' | 'roadtrip' = 'destination';
  searchDestination = '';
  searchStartingLocation = '';
  searchRoadtripDestination = '';
  destSelection: IHeroLocationResult | null = null;
  startSelection: IHeroLocationResult | null = null;
  roadtripDestSelection: IHeroLocationResult | null = null;
  roadtripError = '';
  isSubmittingRoadtrip = false;

  videoPlaying = true;
  /** True unless prefers-reduced-motion is on. Renders <source> so the
   *  <video autoplay ...> can start. Gated in ngAfterViewInit because
   *  matchMedia isn't available during SSR; safe to start true — the
   *  browser flips it to false before autoplay would otherwise begin. */
  allowHeroVideo = true;
  readonly today = new Date();

  // Rig State
  private rigDropdownOpen = false;
  get isRigDropdownOpen(): boolean {
    return this.rigDropdownOpen;
  }
  set isRigDropdownOpen(open: boolean) {
    this.rigDropdownOpen = open;
    this.syncScrollLock();
  }
  rigTypes = ['Class A', 'Class B', 'Class C', 'Fifth Wheel', 'Travel Trailer', 'Truck Camper', 'Popup Camper'];
  /** `length` is nullable so an empty field reads as "not set" rather than 0 —
   *  the input then shows its placeholder instead of a literal 0, and typing 0
   *  is not immediately erased by a write-back. */
  rigSelection: { type: string; length: number | null; slideOuts: number; towing: boolean } = {
    type: '',
    length: null,
    slideOuts: 0,
    towing: false,
  };
  readonly rvTypeLabel = rvTypeLabel;

  /** Saved RV profiles for the signed-in user. When there are any they are
   *  offered above the builder as a one-tap shortcut, but the manual
   *  type/length/slide-outs/towing builder stays reachable underneath — a saved
   *  rig is not the only rig someone may be searching for (user-test F5). */
  get rvProfiles(): IMyRvProfile[] {
    return this.authService.currentUser ? listMyRvProfiles(this.platformId) : [];
  }

  get user(): IPublicUser | null {
    return this.authService.currentUser ?? null;
  }

  get activeRvProfileId(): string | null {
    return getActiveRvProfileId(this.platformId);
  }

  selectRvProfile(profile: IMyRvProfile): void {
    setActiveRvProfile(this.platformId, profile.syncId ?? '');
    this.rigSelection.type = rvTypeLabel(profile.type);
    this.rigSelection.length = profile.length ?? null;
    this.isRigDropdownOpen = false;
  }

  rvInitials(name: string | undefined): string {
    if (!name) return '?';
    return name.split(/\s+/).filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase() || '?';
  }

  get rigDisplayText(): string {
    if (!this.rigSelection.type && !this.rigSelection.length) return 'Type & Length';
    const parts = [];
    if (this.rigSelection.type) parts.push(this.rigSelection.type);
    if (this.rigSelection.length) parts.push(`${this.rigSelection.length}ft`);
    return parts.join(' • ');
  }

  // Date State
  private dateDropdownOpen = false;
  get isDateDropdownOpen(): boolean {
    return this.dateDropdownOpen;
  }
  set isDateDropdownOpen(open: boolean) {
    this.dateDropdownOpen = open;
    this.syncScrollLock();
  }
  selectedDateRange: IDateRange | null = null;

  get dateDisplayText(): string {
    if (!this.selectedDateRange || !this.selectedDateRange.start) return 'Add dates';
    if (!this.selectedDateRange.end) {
      return this.selectedDateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return formatDateRange(this.selectedDateRange.start, this.selectedDateRange.end);
  }

  executeSearch(event?: Event): void {
    if (event) event.stopPropagation();

    if (this.searchMode === 'roadtrip') {
      void this.executeRoadtripSearch();
      return;
    }

    interface HeroSearchQueryParams {
      mode: 'destination' | 'roadtrip';
      dest?: string;
      lat?: number;
      lon?: number;
      zoom?: number;
      startDate?: string;
      endDate?: string;
      rigType?: string;
      rigLength?: number;
      rigSlideOuts?: number;
      rigTowing?: boolean;
    }
    const queryParams: HeroSearchQueryParams = { mode: 'destination' };

    if (this.searchDestination) {
      queryParams.dest = this.searchDestination;
      if (this.destSelection) {
        queryParams.lat = this.destSelection.lat;
        queryParams.lon = this.destSelection.lon;
        queryParams.zoom = 12;
      }
    }

    if (this.selectedDateRange?.start) {
      queryParams.startDate = this.selectedDateRange.start.toISOString();
      if (this.selectedDateRange.end) {
        queryParams.endDate = this.selectedDateRange.end.toISOString();
      }
    }

    if (this.rigSelection.type) queryParams.rigType = this.rigSelection.type;
    if (this.rigSelection.length && this.rigSelection.length > 0) queryParams.rigLength = this.rigSelection.length;
    if (this.rigSelection.slideOuts > 0) queryParams.rigSlideOuts = this.rigSelection.slideOuts;
    if (this.rigSelection.towing) queryParams.rigTowing = true;

    this.router.navigate(['/search'], { queryParams });
  }

  /** Resolves a roadtrip from/to field into a trip-planner stop: a real
   *  listing/boondock reference when the typeahead pick was one, a custom
   *  lat/lng pin otherwise (geocoding free-typed text that was never picked
   *  from a suggestion). Returns null when the location can't be placed. */
  private async resolveRoadtripStop(
    text: string,
    selection: IHeroLocationResult | null,
  ): Promise<Omit<ITripStop, 'id'> | null> {
    if (selection) {
      if (selection.id !== undefined) {
        return {
          kind: selection.isBoondock ? 'boondocking' : 'private',
          refId: selection.id,
          name: selection.name,
          lat: selection.lat,
          lng: selection.lon,
        };
      }
      return { kind: 'custom', name: selection.name, lat: selection.lat, lng: selection.lon };
    }
    const trimmed = text.trim();
    if (!trimmed) return null;
    try {
      const geo = await firstValueFrom(this.geocoding.geocode(trimmed));
      if (!geo) return null;
      return { kind: 'custom', name: trimmed, address: trimmed, lat: geo.lat, lng: geo.lng };
    } catch {
      return null;
    }
  }

  private async executeRoadtripSearch(): Promise<void> {
    if (this.isSubmittingRoadtrip) return;
    this.roadtripError = '';

    const fromText = this.searchStartingLocation.trim();
    const toText = this.searchRoadtripDestination.trim();
    if (!fromText || !toText) {
      this.roadtripError = 'Enter both a starting location and a destination.';
      return;
    }

    this.isSubmittingRoadtrip = true;
    const [startStop, destStop] = await Promise.all([
      this.resolveRoadtripStop(fromText, this.startSelection),
      this.resolveRoadtripStop(toText, this.roadtripDestSelection),
    ]);
    this.isSubmittingRoadtrip = false;

    if (!startStop || !destStop) {
      const badText = !startStop ? fromText : toText;
      this.roadtripError = `Couldn't locate "${badText}" — try picking a suggestion.`;
      return;
    }

    if (this.user && this.rvProfiles.length === 0 && this.rigSelection.type) {
      const typeId = RV_TYPES.find(t => t.id === this.rigSelection.type)?.id
        ?? RV_TYPES.find(t => t.label === this.rigSelection.type)?.id;
      // One call: addMyRvProfile syncs to the backend itself.
      addMyRvProfile(this.platformId, `${this.rigSelection.type} Rig`, {
        type: typeId,
        length: this.rigSelection.length ? this.rigSelection.length : undefined,
      });
    }

    const plan = this.tripPlanner.create(autoTripName());
    if (this.selectedDateRange?.start) {
      this.tripPlanner.update(plan.id, {
        startDate: formatIsoDate(this.selectedDateRange.start),
        endDate: formatIsoDate(this.selectedDateRange.end),
      });
    }
    this.tripPlanner.addStop(plan.id, startStop);
    this.tripPlanner.addStop(plan.id, destStop);
    this.router.navigate(['/trip-planner', plan.id]);
  }

  setSearchMode(mode: 'destination' | 'roadtrip'): void {
    this.searchMode = mode;
    this.isRigDropdownOpen = false;
    this.isDateDropdownOpen = false;
    this.roadtripError = '';
  }

  // Date Methods
  private makeRange(start: Date | null, end: Date | null): IDateRange {
    return { start, end };
  }

  onDateSelected(date: Date) {
    if (!this.selectedDateRange || !this.selectedDateRange.start || (this.selectedDateRange.start && this.selectedDateRange.end)) {
      this.selectedDateRange = this.makeRange(date, null);
    } else if (date < this.selectedDateRange.start) {
      this.selectedDateRange = this.makeRange(date, null);
    } else {
      this.selectedDateRange = this.makeRange(this.selectedDateRange.start, date);
      this.dateCloseTimer = setTimeout(() => {
        this.isDateDropdownOpen = false;
        this.dateCloseTimer = null;
      }, 250);
    }
  }

  toggleDateDropdown(event: Event): void {
    event.stopPropagation();
    this.isDateDropdownOpen = !this.isDateDropdownOpen;
    if (this.isDateDropdownOpen) this.isRigDropdownOpen = false;
  }

  // Rig Methods
  toggleRigDropdown(event: Event): void {
    event.stopPropagation();
    this.isRigDropdownOpen = !this.isRigDropdownOpen;
    if (this.isRigDropdownOpen) this.isDateDropdownOpen = false;
  }

  selectRigType(type: string): void {
    this.rigSelection.type = this.rigSelection.type === type ? '' : type;
  }

  /** Length is typed, not stepped — a 34ft rig took 34 clicks before (user-test
   *  F5). Keeps the value a non-negative whole number without clamping to a
   *  maximum, which would fight the digits still being typed. */
  setRigLength(value: number | string | null): void {
    if (value === null || value === '') {
      this.rigSelection.length = null;
      return;
    }
    const feet = Math.floor(Number(value));
    this.rigSelection.length = Number.isFinite(feet) ? Math.max(0, feet) : null;
  }

  updateSlideOuts(val: number): void {
    this.rigSelection.slideOuts = Math.max(0, this.rigSelection.slideOuts + val);
  }

  toggleTowing(): void {
    this.rigSelection.towing = !this.rigSelection.towing;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Phones present the pickers as a sheet, which dismisses itself (backdrop,
    // Escape, Done) and renders outside .rig-dropdown-container — so this
    // outside-click guard would close it on every tap inside it. It exists for
    // the anchored dropdown only.
    if (this.isPhoneViewport) return;
    const target = event.target as HTMLElement;
    if (this.isRigDropdownOpen && !target.closest('.rig-dropdown-container') && !target.closest('.cnt-glass-picker')) {
      this.isRigDropdownOpen = false;
    }
    if (this.isDateDropdownOpen && !target.closest('.rig-dropdown-container') && !target.closest('.cnt-glass-picker')) {
      this.isDateDropdownOpen = false;
    }
  }

  /** Just the play/pause switch — bound directly by the visible chip
   *  button (keyboard-accessible entry point) and delegated to from the
   *  sticky-wrap-hero click handler (mouse-only convenience). */
  togglePlayback(): void {
    const el = this.heroVideoRef?.nativeElement;
    if (!el) return;
    if (this.videoPlaying) {
      el.pause();
    } else {
      el.play().catch(() => { /* noop — autoplay policy or user gesture */ });
    }
    this.videoPlaying = !this.videoPlaying;
  }

  toggleVideo(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.btn-3d-wrap') || target.closest('button')) {
      return;
    }
    this.togglePlayback();
  }

  /** Phones show the When / Your Rig pickers as a bottom sheet: the dropdown
   *  hangs below the search card, which on a phone is already low in the hero,
   *  so it opened below the fold. false during SSR, so the server renders the
   *  desktop dropdown. */
  isPhoneViewport = false;
  private phoneQuery: MediaQueryList | null = null;
  private onPhoneQueryChange: ((e: MediaQueryListEvent) => void) | null = null;

  private watchPhoneViewport(): void {
    this.phoneQuery = window.matchMedia('(max-width: 767.98px)');
    this.isPhoneViewport = this.phoneQuery.matches;
    this.onPhoneQueryChange = (e: MediaQueryListEvent) => {
      this.isPhoneViewport = e.matches;
      // Presentation swaps mid-gesture on rotate otherwise (sheet -> dropdown
      // with the picker still open, in a spot that may be off-screen).
      this.closeDropdowns();
    };
    this.phoneQuery.addEventListener('change', this.onPhoneQueryChange);
  }

  /** Holds the page still behind an open sheet. Same `cnt-modal-open` contract
   *  the other modals use (styles.scss: body overflow hidden, navbar hidden,
   *  stable scrollbar gutter). Phones only — the md+ dropdown scrolls with the
   *  page by design, and hiding the navbar there would be wrong. Driven from the
   *  two setters above, so every open/close path stays in step. */
  private syncScrollLock(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const locked = this.isPhoneViewport && (this.dateDropdownOpen || this.rigDropdownOpen);
    document.body.classList.toggle('cnt-modal-open', locked);
  }

  /** Shared by the sheet's Done button, its backdrop/Escape, and rotation. */
  closeDropdowns(): void {
    this.isDateDropdownOpen = false;
    this.isRigDropdownOpen = false;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.watchPhoneViewport();
    // Reduced-motion OR mobile: flip allowHeroVideo off — [autoplay] +
    // [preload] on the <video> both go to their disabled branches
    // (false / 'none'), so the source URL sits in the DOM but is
    // never fetched. On mobile the video circle itself is hidden by
    // the component SCSS's `@media (max-width: 1023px) { .sticky-content
    // { display: none } }` rule, so we save ~4 MB per pageview and
    // skip the sticky-scroll choreography that only reads well at md+.
    // Also skip the hero text fly-in / sticky-expand animations below.
    // WCAG 2.3.3 preserved for reduced-motion.
    // Matches the SCSS: the stacked hero (no video circle) below lg and on
    // phones held sideways.
    const isMobile =
      window.innerWidth < 1024 || window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches;
    if (prefersReducedMotion() || isMobile) {
      this.allowHeroVideo = false;
      return;
    }
    // Three-leg .play() retry (immediate / loadedmetadata / canplay)
    // as defense in depth against genuine race conditions between
    // hydration, metadata arrival, and the browser's autoplay gate.
    // Native `autoplay` on the <video> attribute handles most cases;
    // this exists for the small tail of paths where the first attempt
    // gets a rejection because metadata isn't ready yet.
    const heroVideoEl = this.heroVideoRef?.nativeElement;
    if (heroVideoEl) {
      // Reset once — clears any "autoplay refused" flag Chrome may
      // have latched during hydration and re-evaluates the sources.
      heroVideoEl.load();
      const tryPlay = () => {
        // Force muted state before every attempt. Chrome only permits
        // muted autoplay; defaultMuted is what it persists across
        // .load() cycles, so setting both makes the gate stable.
        heroVideoEl.muted = true;
        heroVideoEl.defaultMuted = true;
        return heroVideoEl.play().catch(() => {
          /* Autoplay refused (rare after the fixes above — most
             common cause is iOS Low-Power Mode or a browser-level
             autoplay block for this origin). The play/pause chip
             and the sticky-wrap click handler give the user a way in. */
        });
      };
      tryPlay();
      heroVideoEl.addEventListener('loadedmetadata', tryPlay, { once: true });
      heroVideoEl.addEventListener('canplay', tryPlay, { once: true });
    }
    // P86 — dynamic-import gsap. The hero's initial state is now CSS-driven,
    // so the module can arrive after paint without a visible pop.
    (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);
      this.gsap = gsap;
      this.ST = ScrollTrigger;
      this.gsap.registerPlugin(this.ST);
      this.initHeroEntry();
      this.initHeroExpand();
    })();
  }

  private initHeroEntry(): void {
    // P86 — initial state (y: 150, rotateX: 20) is now set via CSS on
    // `.hero-span`; gsap only animates FROM that state to visible.
    const words = this.gsap.utils.toArray('.hero-span');
    this.gsap.to(words, {
      y: 0,
      rotateX: 0,
      duration: 1.2,
      stagger: 0.1,
      ease: 'back.out(1.5)',
      delay: 0.1
    });

    const stickyContent = document.querySelector('.sticky-content');
    if (stickyContent) {
      // Initial `scale: 0` is set via CSS on `.sticky-content`.
      this.gsap.to(stickyContent,
        { scale: 1, duration: 1.5, ease: 'elastic.out(1, 0.5)', delay: 0.1 }
      );
    }
  }

  private initHeroExpand(): void {
    const stickyContent = document.querySelector('.sticky-content') as HTMLElement;
    const stickyWrap = document.querySelector('.sticky-wrap-hero') as HTMLElement;
    if (!stickyContent || !stickyWrap) return;

    const update = () => {
      const rect = stickyWrap.getBoundingClientRect();
      // Use actual scroll runway. `.sticky-wrap-hero` is height:250vh so
      // runway = 1.5 viewports — animation completes naturally before the
      // sticky element unsticks. Floor at 1 to avoid divide-by-zero on
      // edge cases where wrap collapses below viewport.
      const totalScroll = Math.max(stickyWrap.offsetHeight - window.innerHeight, 1);
      const scrolled = -rect.top;
      const p = Math.max(0, Math.min(1, scrolled / totalScroll));

      const vmin = Math.min(window.innerWidth, window.innerHeight);
      const startPx = vmin * 0.50;

      // Match the navbar pill's rendered width at full expansion so the
       // hero ends visually flush with the navbar (capped at 90rem).
      const navbar = document.querySelector('.block-navbar') as HTMLElement | null;
      const targetWidth = navbar ? navbar.getBoundingClientRect().width : window.innerWidth;
      const targetHeight = window.innerHeight;

      const currentW = startPx + (targetWidth - startPx) * (Math.pow(p, 2));
      let currentH = startPx + (targetHeight - startPx) * (Math.pow(p, 2));

      const vhOffset = window.innerHeight * 0.5 * p;
      currentH += vhOffset;

      const clampH = Math.min(currentH, targetHeight);

      const borderRadius = 50 * (1 - p);

      // No JS offset needed — `.sticky-element` is justify-content:
      // flex-end inside .hero-wrap (max-width: 90rem, centered), so the
      // pill sits at the right edge of the same 90rem container as the
      // navbar. As width grows toward 90rem the pill expands leftward
      // and at p=1 fills the container exactly, edges flush with the
      // navbar pill.
      stickyContent.style.width = currentW + 'px';
      stickyContent.style.height = clampH + 'px';
      stickyContent.style.borderRadius = borderRadius + '%';
      // Inset from the right edge at p=0 (Webflow start aesthetic);
      // collapses to 0 at p=1 so the pill ends flush with the navbar.
      stickyContent.style.marginRight = (32 * (1 - p)) + 'px';
    };

    update();
    this.heroUpdate = update;
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  ngOnDestroy(): void {
    if (this.heroUpdate) {
      window.removeEventListener('scroll', this.heroUpdate);
      window.removeEventListener('resize', this.heroUpdate);
      this.heroUpdate = null;
    }
    if (this.dateCloseTimer) {
      clearTimeout(this.dateCloseTimer);
      this.dateCloseTimer = null;
    }
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('cnt-modal-open');
    }
    if (this.phoneQuery && this.onPhoneQueryChange) {
      this.phoneQuery.removeEventListener('change', this.onPhoneQueryChange);
      this.phoneQuery = null;
      this.onPhoneQueryChange = null;
    }
  }
}
